import {
  Building2,
  Edit,
  Mail,
  Phone,
  User,
  UserMinus,
  UserPlus,
  UserX,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { Manager } from "../../../types/manager.definition";
import { ActionMenu, ActionMenuItem } from "../ActionMenu/ActionMenu";
import { StatusBadge } from "../StatusBadge/StatusBadge";

interface ManagerCardProps {
  manager: Manager;
  onClick: (manager: Manager) => void;
  onAssignFacility?: (manager: Manager) => void;
  onUnassignFacility?: (manager: Manager) => void;
  onEdit?: (manager: Manager) => void;
  onDisable?: (manager: Manager) => void;
}

export const ManagerCard: React.FC<ManagerCardProps> = ({
  manager,
  onClick,
  onAssignFacility,
  onUnassignFacility,
  onEdit,
  onDisable,
}) => {
  const { t } = useTranslation();

  const handleCardClick = () => {
    onClick(manager);
  };

  // MED-1470: the row actions. Every entry is wired to a real handler — a menu item
  // without one is left out rather than rendered dead.
  const actions: ActionMenuItem[] = [
    onEdit && {
      label: t("common.edit"),
      icon: <Edit className="w-4 h-4" />,
      onClick: () => onEdit(manager),
    },
    onAssignFacility && {
      label: t("admin.managers.assign"),
      icon: <UserPlus className="w-4 h-4" />,
      onClick: () => onAssignFacility(manager),
    },
    onUnassignFacility && {
      label: t("common.unassign"),
      icon: <UserMinus className="w-4 h-4" />,
      // Nothing to detach from when no facility is assigned.
      disabled: !manager.healthcareFacility,
      onClick: () => onUnassignFacility(manager),
    },
    // MED-1515: this entry was labelled "Delete" but its handler calls
    // DELETE /managers/{id}, which the API deliberately implements as a soft
    // disable — manager accounts are FK-referenced by appointments and are
    // never erased. Label it for what it does, matching the detail page's own
    // disable control, so no administrator can believe an account is gone.
    onDisable && {
      label: t("common.disable"),
      icon: <UserX className="w-4 h-4" />,
      danger: true,
      onClick: () => onDisable(manager),
    },
  ].filter(Boolean) as ActionMenuItem[];

  return (
    <div
      className="flex flex-col p-4 w-full transition-all duration-200 cursor-pointer card-container hover:shadow-lg group"
      onClick={handleCardClick}
    >
      <div className="flex items-start space-x-4 w-full">
        {/* Manager Avatar */}
        <div className="flex-shrink-0">
          <div className="relative">
            <div className="flex overflow-hidden justify-center items-center w-16 h-16 bg-gradient-to-br rounded-full from-primary-100 to-primary-200">
              {manager.photoUrl ? (
                <img
                  src={manager.photoUrl}
                  alt={`${manager.firstName} ${manager.lastName}`}
                  className="object-cover w-full h-full rounded-full"
                />
              ) : (
                <User className="w-8 h-8 text-primary-600" />
              )}
            </div>
            {/* Status indicator */}
            <div className="flex absolute -right-1 -bottom-1 justify-center items-center w-5 h-5 rounded-full border-2 border-white bg-success-500">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Manager Info */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate transition-colors text-neutral-900 group-hover:text-primary-600">
                {manager.firstName} {manager.lastName}
              </h3>
              <p className="mb-2 text-sm truncate text-neutral-600">
                {manager.email}
              </p>

              {/* Contact Info */}
              <div className="flex flex-wrap gap-y-1 gap-x-4 items-center mb-3 text-xs text-neutral-500">
                {manager.phoneNumber && (
                  <div className="flex items-center space-x-1 min-w-0">
                    <Phone className="flex-shrink-0 w-3 h-3" />
                    <span className="truncate">
                      {formatPhoneNumberForDisplay(manager.phoneNumber)}
                    </span>
                  </div>
                )}
                <div className="flex items-center space-x-1 min-w-0">
                  <Mail className="flex-shrink-0 w-3 h-3" />
                  <span className="truncate">{manager.email}</span>
                </div>
              </div>

              {/* Facility Assignment */}
              <div className="flex items-center min-w-0">
                {manager.healthcareFacility ? (
                  <div className="flex items-center space-x-2 min-w-0">
                    <Building2 className="flex-shrink-0 w-4 h-4 text-success-500" />
                    <span className="text-sm font-medium truncate text-success-700">
                      {manager.healthcareFacility.name}
                    </span>
                    <StatusBadge status={t("common.statuses.active")} />
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 min-w-0">
                    <Building2 className="flex-shrink-0 w-4 h-4 text-neutral-400" />
                    <span className="text-sm truncate text-neutral-500">
                      {t("admin.managers.noFacilityAssigned")}
                    </span>
                    <StatusBadge status={t("common.statuses.inactive")} />
                  </div>
                )}
              </div>
            </div>
            {actions.length > 0 && (
              <div
                className="flex-shrink-0"
                onClick={event => event.stopPropagation()}
              >
                <ActionMenu
                  items={actions}
                  ariaLabel={t("admin.managers.actionsMenu")}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Info Row */}
      <div className="pt-4 mt-4 border-t border-neutral-100">
        <div className="flex flex-wrap gap-2 justify-between items-center text-xs text-neutral-500">
          <div className="flex flex-wrap gap-y-1 gap-x-4 items-center min-w-0">
            <span className="truncate">
              {t("admin.managers.role")}:{" "}
              <span className="font-medium text-neutral-700">
                {manager.roleName}
              </span>
            </span>
            {manager.dateOfBirth && (
              <span>
                {t("admin.managers.age")}:{" "}
                <span className="font-medium text-neutral-700">
                  {new Date().getFullYear() -
                    new Date(manager.dateOfBirth).getFullYear()}
                </span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            {manager.preferredNotificationMethods?.map(method => (
              <span
                key={method}
                className="px-2 py-1 text-xs rounded-full bg-neutral-100 text-neutral-600"
              >
                {method}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
